import { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import { Formik, Form, useField } from 'formik';
import Sidebar from '../../components/AdminDashboard/Sidebar';
import TopBar from '../../components/AdminDashboard/TopBar';
import MetaData from '../../layouts/MetaData';
import Loader from '../../layouts/Loader';
import { Prod01 } from '../../constants/ActionType';
import { isSuperAdmin } from '../../components/Form/StaffOrgAssign';
import TextField from '../../components/Form/TextField';
import StateSelectBox from '../../components/Form/StateSelectBox';
import MaskedField, { PhoneField, ZipField, formatPhone } from '../../components/Form/MaskedFields';
import { useSortableTable, SortableTh } from '../../components/SortableTable';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

const ORG_SORT_COLUMNS = {
    name: { type: 'string', value: (o) => o?.name },
    code: { type: 'string', value: (o) => o?.slug },
    npi: { type: 'string', value: (o) => o?.practiceNpi },
    status: { type: 'string', value: (o) => o?.status },
    created: { type: 'date', value: (o) => o?.createdAt },
};

const errMsg = (error) => error?.response?.data?.message || error.message;
const digits = (v) => (v || '').replace(/\D/g, '');
const formatTin = (d) => {
  const x = digits(d).slice(0, 9);
  return x.length <= 2 ? x : `${x.slice(0, 2)}-${x.slice(2)}`;
};
const TIME_ZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Puerto_Rico',
];
const FIELD_LABELS = {
  legalName: 'Legal name',
  tin: 'TIN',
  practiceNpi: 'Practice NPI',
  phone: 'Phone',
  'address.street1': 'Street',
  'address.city': 'City',
  'address.state': 'State',
  'address.zip': 'ZIP',
  primaryContactName: 'Primary contact',
  primaryContactEmail: 'Primary contact email',
  medicalDirectorName: 'Medical director',
};

// Flat form values (dotted address keys) from an org record.
const toValues = (o = {}) => ({
  name: o.name || '',
  code: o.slug || '',
  legalName: o.legalName || '',
  dba: o.dba || '',
  tin: '',
  practiceNpi: o.practiceNpi || '',
  phone: formatPhone(o.phone || ''),
  fax: formatPhone(o.fax || ''),
  street1: o.address?.street1 || '',
  street2: o.address?.street2 || '',
  city: o.address?.city || '',
  state: o.address?.state || '',
  zip: o.address?.zip || '',
  primaryContactName: o.primaryContactName || '',
  primaryContactEmail: o.primaryContactEmail || '',
  medicalDirectorName: o.medicalDirectorName || '',
  timeZone: o.timeZone || 'America/New_York',
  onboardingDate: o.onboardingDate ? moment(o.onboardingDate).format('YYYY-MM-DD') : '',
});

const toBody = (v) => ({
  name: v.name.trim(),
  code: v.code.trim(),
  legalName: v.legalName.trim(),
  dba: v.dba.trim(),
  tin: digits(v.tin),
  practiceNpi: v.practiceNpi.trim(),
  phone: digits(v.phone),
  fax: digits(v.fax),
  address: { street1: v.street1.trim(), street2: v.street2.trim(), city: v.city.trim(), state: v.state, zip: v.zip.trim() },
  primaryContactName: v.primaryContactName.trim(),
  primaryContactEmail: v.primaryContactEmail.trim(),
  medicalDirectorName: v.medicalDirectorName.trim(),
  timeZone: v.timeZone,
  onboardingDate: v.onboardingDate,
});

// Edit sends only changed fields (legacy orgs stay editable without completing the profile).
const changedBody = (initial, values) => {
  const a = toBody(initial);
  const b = toBody(values);
  const out = {};
  Object.keys(b).forEach((k) => {
    if (k === 'address') {
      const addr = {};
      Object.keys(b.address).forEach((f) => {
        if (b.address[f] !== a.address[f]) addr[f] = b.address[f];
      });
      if (Object.keys(addr).length) out.address = addr;
    } else if (k === 'tin') {
      if (b.tin) out.tin = b.tin;
    } else if (b[k] !== a[k]) out[k] = b[k];
  });
  if (out.code === '') delete out.code;
  return out;
};

const validateCreate = (v) => {
  const e = {};
  const req = ['legalName', 'practiceNpi', 'phone', 'street1', 'city', 'state', 'zip', 'primaryContactName', 'primaryContactEmail', 'medicalDirectorName'];
  req.forEach((k) => {
    if (!String(v[k] || '').trim()) e[k] = 'Required';
  });
  if (digits(v.tin).length !== 9) e.tin = 'TIN must be 9 digits';
  return e;
};

const validateAll = (v) => {
  const e = {};
  if (v.practiceNpi && !/^\d{10}$/.test(v.practiceNpi.trim())) e.practiceNpi = 'NPI must be 10 digits';
  if (v.phone && digits(v.phone).length !== 10) e.phone = 'Phone must be 10 digits';
  if (v.fax && digits(v.fax).length !== 10) e.fax = 'Fax must be 10 digits';
  if (v.zip && !/^\d{5}$/.test(v.zip)) e.zip = 'ZIP must be 5 digits';
  if (v.tin && digits(v.tin).length !== 9) e.tin = 'TIN must be 9 digits';
  if (v.primaryContactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.primaryContactEmail.trim())) e.primaryContactEmail = 'Invalid email';
  return e;
};

const ProfileBadge = ({ org }) =>
  org.profileComplete ? (
    <span className="badge badge-success">complete</span>
  ) : (
    <span className="badge badge-warning" title={`Missing: ${(org.missingFields || []).map((f) => FIELD_LABELS[f] || f).join(', ')}`}>
      profile incomplete
    </span>
  );

// Super-Admin Organizations screen: list, create, profile edit, deactivate/reactivate with status history.
const Organizations = () => {
  const { user } = useSelector((state) => state.auth);
  const alert = useAlert();
  const [orgs, setOrgs] = useState(null);
  const tableSort = useSortableTable(orgs, { id: 'admin.organizations', columns: ORG_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
  const [editing, setEditing] = useState(null); // org being edited, or null for create
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revealedTin, setRevealedTin] = useState(null);
  const [npiInfo, setNpiInfo] = useState(null);
  const [statusDialog, setStatusDialog] = useState(null); // { target: 'active'|'inactive' }
  const [reason, setReason] = useState('');

  const load = () =>
    axios
      .get(`${Prod01}/organizations`)
      .then(({ data }) => setOrgs(data || []))
      .catch((e) => {
        setOrgs([]);
        alert.error(errMsg(e));
      });

  useEffect(() => {
    if (isSuperAdmin(user)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSuperAdmin(user)) {
    return (
      <>
        <MetaData title="Organizations" />
        <Sidebar />
        <section className="home-section">
          <TopBar />
          <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card">
            <p className="text-danger">Super Admin only.</p>
          </div>
        </section>
      </>
    );
  }

  const resetSide = () => {
    setRevealedTin(null);
    setNpiInfo(null);
  };
  const openCreate = () => {
    setEditing(null);
    resetSide();
    setShow(true);
  };
  const openEdit = async (o) => {
    resetSide();
    try {
      const { data } = await axios.get(`${Prod01}/organizations/${o._id}`);
      setEditing(data);
      setShow(true);
    } catch (err) {
      alert.error(errMsg(err));
    }
  };

  const submit = async (values) => {
    setSaving(true);
    try {
      if (editing) {
        const body = changedBody(toValues(editing), values);
        if (!Object.keys(body).length) {
          alert.info('No changes');
          return;
        }
        const { data } = await axios.put(`${Prod01}/organizations/${editing._id}`, body);
        setEditing(data);
        alert.success('Organization updated');
      } else {
        const body = toBody(values);
        ['name', 'code', 'dba', 'fax', 'onboardingDate'].forEach((k) => {
          if (!body[k]) delete body[k];
        });
        if (!body.address.street2) delete body.address.street2;
        await axios.post(`${Prod01}/organizations`, body);
        alert.success('Organization created');
        setShow(false);
      }
      await load();
    } catch (err) {
      alert.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const reveal = async () => {
    try {
      const { data } = await axios.post(`${Prod01}/organizations/${editing._id}/tin/reveal`);
      setRevealedTin(data.tin || '(none on file)');
    } catch (err) {
      alert.error(errMsg(err));
    }
  };

  const verifyNpi = async (npi) => {
    setNpiInfo({ loading: true });
    try {
      const { data } = await axios.get(`${Prod01}/organizations/npi/${encodeURIComponent(npi.trim())}/lookup`);
      setNpiInfo(data);
    } catch (err) {
      setNpiInfo({ error: errMsg(err) });
    }
  };

  const changeStatus = async () => {
    const target = statusDialog.target;
    if (target === 'inactive' && !reason.trim()) {
      alert.error('A reason is required');
      return;
    }
    setSaving(true);
    try {
      const action = target === 'inactive' ? 'deactivate' : 'reactivate';
      const { data } = await axios.post(`${Prod01}/organizations/${editing._id}/${action}`, { reason: reason.trim() });
      setEditing(data);
      setStatusDialog(null);
      setReason('');
      alert.success(`"${data.name}" is now ${data.status}`);
      await load();
    } catch (err) {
      alert.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const isCreate = !editing;

  return (
    <>
      <MetaData title="Organizations" />
      <Sidebar />
      <section className="home-section">
        <TopBar />
        <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card">
          <div className="home-content">
            <div className="row-display">
              <h5 style={{ color: '#004aad' }}>
                <i className="bx bx-buildings"></i> <strong>&nbsp;Organizations</strong>
              </h5>
              <span className="edit-pill" onClick={openCreate}>
                <i className="bx bx-plus"></i> New Organization
              </span>
            </div>
            <hr />
            {orgs === null ? (
              <Loader />
            ) : (
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <SortableTh sortKey="name" sort={tableSort}>Name</SortableTh>
                    <SortableTh sortKey="code" sort={tableSort}>Code</SortableTh>
                    <SortableTh sortKey="npi" sort={tableSort}>Practice NPI</SortableTh>
                    <th>TIN</th>
                    <SortableTh sortKey="status" sort={tableSort}>Status</SortableTh>
                    <th>Profile</th>
                    <SortableTh sortKey="created" sort={tableSort}>Created</SortableTh>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {tableSort.rows.map((o) => (
                    <tr key={o._id}>
                      <td>
                        {o.name}
                        {o.legalName && o.legalName !== o.name ? (
                          <>
                            <br />
                            <small className="text-muted">{o.legalName}</small>
                          </>
                        ) : null}
                      </td>
                      <td>
                        <code>{o.slug}</code>
                      </td>
                      <td>{o.practiceNpi || '-'}</td>
                      <td>{o.tinMasked || '-'}</td>
                      <td>
                        <span className={`badge ${o.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>{o.status}</span>
                      </td>
                      <td>
                        <ProfileBadge org={o} />
                      </td>
                      <td>{o.createdAt ? formatDate(o.createdAt) : '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn btn-primary btn-sm m-1" onClick={() => openEdit(o)}>
                          <i className="bx bx-edit"></i> Open
                        </button>
                      </td>
                    </tr>
                  ))}
                  {orgs.length === 0 ? (
                    <tr>
                      <td colSpan={8}>No organizations yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            )}
            <small className="text-muted">
              Assign staff to organizations from each Admin / Doctor / Nurse profile (Organization(s) field). Patients and devices are scoped separately.
            </small>
          </div>
        </div>

        <Modal size="xl" show={show} onHide={() => setShow(false)}>
          <Modal.Header closeButton>
            {editing ? (
              <>
                {editing.name}&nbsp;
                <span className={`badge ${editing.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>{editing.status}</span>&nbsp;
                <ProfileBadge org={editing} />
              </>
            ) : (
              'New Organization'
            )}
          </Modal.Header>
          <Modal.Body>
            <Formik
              key={editing ? `${editing._id}-${editing.updatedAt}` : 'new'}
              initialValues={toValues(editing || {})}
              validate={(v) => ({ ...(isCreate ? validateCreate(v) : {}), ...validateAll(v) })}
              onSubmit={submit}
            >
              {({ values }) => (
                <Form>
                  <div className="row">
                    <div className="col-md-4">
                      <TextField label="Display name" name="name" type="text" placeholder="defaults to DBA / legal name" />
                    </div>
                    <div className="col-md-4">
                      <TextField label="Code / slug" name="code" type="text" placeholder="auto-generated if blank" />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label mt-3">Time zone</label>
                      <TimeZoneSelect name="timeZone" />
                    </div>
                    <div className="col-md-6">
                      <TextField label="Legal name" compulsory={isCreate} name="legalName" type="text" />
                    </div>
                    <div className="col-md-6">
                      <TextField label="DBA" name="dba" type="text" />
                    </div>
                    <div className="col-md-4">
                      <MaskedField
                        label={editing ? `TIN (EIN) — on file: ${editing.tinMasked || 'none'}` : 'TIN (EIN)'}
                        compulsory={isCreate}
                        name="tin"
                        maxDigits={9}
                        formatter={formatTin}
                        placeholder={editing ? 'leave blank to keep' : 'XX-XXXXXXX'}
                      />
                      {editing && editing.tinMasked ? (
                        <small>
                          {revealedTin ? (
                            <span>
                              Full TIN: <strong>{revealedTin}</strong> (audited)
                            </span>
                          ) : (
                            <button type="button" className="btn btn-link btn-sm p-0" onClick={reveal}>
                              Reveal full TIN (audit-logged)
                            </button>
                          )}
                        </small>
                      ) : null}
                    </div>
                    <div className="col-md-4">
                      <MaskedField label="Practice NPI (Type 2)" compulsory={isCreate} name="practiceNpi" maxDigits={10} placeholder="10 digits" />
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={!/^\d{10}$/.test(values.practiceNpi || '')}
                        onClick={() => verifyNpi(values.practiceNpi)}
                      >
                        Verify NPI
                      </button>
                      <NpiResult info={npiInfo} />
                    </div>
                    <div className="col-md-4">
                      <TextField label="Onboarding date" name="onboardingDate" type="date" />
                    </div>
                    <div className="col-md-4">
                      <PhoneField label="Phone" compulsory={isCreate} name="phone" placeholder="(XXX) XXX-XXXX" />
                    </div>
                    <div className="col-md-4">
                      <PhoneField label="Fax" name="fax" placeholder="(XXX) XXX-XXXX" />
                    </div>
                    <div className="col-md-4" />
                    <div className="col-md-6">
                      <TextField label="Street" compulsory={isCreate} name="street1" type="text" />
                    </div>
                    <div className="col-md-6">
                      <TextField label="Street 2" name="street2" type="text" />
                    </div>
                    <div className="col-md-4">
                      <TextField label="City" compulsory={isCreate} name="city" type="text" />
                    </div>
                    <div className="col-md-4">
                      <StateSelectBox label="State" compulsory={isCreate} name="state" />
                    </div>
                    <div className="col-md-4">
                      <ZipField label="ZIP" compulsory={isCreate} name="zip" placeholder="5 digits" />
                    </div>
                    <div className="col-md-4">
                      <TextField label="Primary contact name" compulsory={isCreate} name="primaryContactName" type="text" />
                    </div>
                    <div className="col-md-4">
                      <TextField label="Primary contact email" compulsory={isCreate} name="primaryContactEmail" type="email" />
                    </div>
                    <div className="col-md-4">
                      <TextField label="Medical director" compulsory={isCreate} name="medicalDirectorName" type="text" />
                    </div>
                  </div>
                  <div className="row-class mt-3" style={{ justifyContent: 'space-between' }}>
                    <button type="button" className="btn btn-secondary btn-sm m-2" onClick={() => setShow(false)}>
                      Close
                    </button>
                    <button type="submit" className="edit-pill" disabled={saving}>
                      {saving ? 'Saving...' : editing ? 'Save profile' : 'Create'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>

            {editing ? (
              <div className="mt-4 p-3" style={{ border: '1px solid #dee2e6', borderRadius: 8 }}>
                <div className="row-display">
                  <h6 className="mb-0">
                    Status:&nbsp;
                    <span className={`badge ${editing.status === 'active' ? 'badge-success' : 'badge-secondary'}`}>{editing.status}</span>
                  </h6>
                  {editing.status === 'active' ? (
                    <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setStatusDialog({ target: 'inactive' })}>
                      Deactivate
                    </button>
                  ) : (
                    <button type="button" className="btn btn-outline-success btn-sm" onClick={() => setStatusDialog({ target: 'active' })}>
                      Reactivate
                    </button>
                  )}
                </div>
                <small className="text-muted">
                  Inactive: staff whose only organization is this one cannot log in; its patients, devices and reports are hidden from everyone except Super
                  Admin. Device telemetry keeps being ingested.
                </small>
                <table className="table table-sm mt-2 mb-0">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Status</th>
                      <th>By</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editing.statusHistory || [])
                      .slice()
                      .reverse()
                      .map((h, i) => (
                        <tr key={`${h.changedAt}-${i}`}>
                          <td>{h.changedAt ? formatDateTime(h.changedAt) : '-'}</td>
                          <td>{h.status}</td>
                          <td>{h.changedByName || '-'}</td>
                          <td>{h.reason || '-'}</td>
                        </tr>
                      ))}
                    {!(editing.statusHistory || []).length ? (
                      <tr>
                        <td colSpan={4} className="text-muted">
                          No status changes recorded.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Modal.Body>
        </Modal>

        <Modal show={!!statusDialog} onHide={() => setStatusDialog(null)} centered>
          <Modal.Header closeButton>{statusDialog?.target === 'inactive' ? 'Deactivate organization' : 'Reactivate organization'}</Modal.Header>
          <Modal.Body>
            <p>
              {statusDialog?.target === 'inactive'
                ? `Deactivate "${editing?.name}"? Its single-organization staff lose access immediately and its data is hidden from everyone except Super Admin until reactivated.`
                : `Reactivate "${editing?.name}"? Its staff regain access immediately.`}
            </p>
            <label className="form-label">
              Reason {statusDialog?.target === 'inactive' ? <span style={{ color: 'red' }}>*</span> : '(optional)'}
            </label>
            <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
            <div className="row-class mt-3" style={{ justifyContent: 'space-between' }}>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setStatusDialog(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={`btn btn-sm ${statusDialog?.target === 'inactive' ? 'btn-danger' : 'btn-success'}`}
                disabled={saving || (statusDialog?.target === 'inactive' && !reason.trim())}
                onClick={changeStatus}
              >
                {statusDialog?.target === 'inactive' ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

// Formik-bound time zone <select>; keeps an unlisted stored value selectable.
const TimeZoneSelect = ({ name }) => {
  const [field] = useField(name);
  const opts = TIME_ZONES.includes(field.value) || !field.value ? TIME_ZONES : [field.value, ...TIME_ZONES];
  return (
    <select className="form-control" {...field}>
      {opts.map((tz) => (
        <option key={tz} value={tz}>
          {tz}
        </option>
      ))}
    </select>
  );
};

const NpiResult = ({ info }) => {
  if (!info) return null;
  if (info.loading) return <div><small>Checking NPI registry...</small></div>;
  if (info.error) return <div><small className="text-danger">{info.error}</small></div>;
  if (!info.available) return <div><small className="text-muted">NPI registry unavailable — saving is not affected.</small></div>;
  if (!info.found) return <div><small className="text-warning">Not found in NPI registry.</small></div>;
  return (
    <div>
      <small>
        Registry: <strong>{info.name}</strong> ({info.type}
        {info.isOrganization ? '' : ' — not a Type 2 organization NPI'}){info.city ? `, ${info.city} ${info.state}` : ''}
      </small>
    </div>
  );
};

export default Organizations;
